import { useEffect, useRef } from 'react';

import { WS_EMOJI_CHAT_STATIC_PART } from 'shared/constants/constants';

export type EmojiMessage = {
  from: string;
  emoji: string;
};

type UseEmojiSocketParams = {
  chainId: string | number;
  roomId: string | number;
  onEmojiReceived?: (msg: EmojiMessage) => void;
};

export const useEmojiSocket = ({ chainId, roomId, onEmojiReceived }: UseEmojiSocketParams) => {
  const socketRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const wsUrl = `${WS_EMOJI_CHAT_STATIC_PART}${chainId}/${roomId}`;

    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket opened!');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.emoji && onEmojiReceived) {
        console.log('Emoji received');
        onEmojiReceived(data);
      }
    };

    ws.onerror = (err) => {
      console.error('WebSocket error', err);
    };

    ws.onclose = () => {
      console.log('WebSocket closed');

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };

    return () => {
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      ws.close();
    };
  }, [chainId, roomId]);

  useEffect(() => {
    const pingInterval = setInterval(() => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);

    return () => clearInterval(pingInterval);
  }, []);

  const sendEmoji = (from: `0x${string}`, emoji: string) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    const message = JSON.stringify({ from, emoji });
    socket.send(message);
  };

  return { sendEmoji };
};
